// 1. React
import React, { useEffect, useState, useCallback } from 'react';

// 2. Third-party
import { Drawer, Form, Input, InputNumber, Select, Switch, Button, Collapse, Space, Popconfirm, Row, Col, Tag, Tooltip, Modal, message } from 'antd';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
  HolderOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  UndoOutlined,
} from '@ant-design/icons';

// 3. Services
import { globalFormService, categoryGlobalFormService, subCategoryGlobalFormService, propertyHostService, formService } from '@iso/services';

// 4. Constants
import { LIBRARY_FORM_TYPE_OPTIONS } from '@iso/constants/libraryFormTypes';
import { getWidgetLabel, bakeHostDefaults } from '@iso/constants/libraryFieldWidgets';

// 5. Local components
import {
  DRAG_SENSOR_OPTIONS,
  SortableBlock,
} from './FormBuilderSortable';
import SelectFromLibraryModal from '@iso/containers/Pages/Form/SelectFromLibraryModal';
import FieldEditorModal from './FieldEditorModal';
import PreviewFormModal from './PreviewFormModal';

const SUCCESS_CODE = '0000';
const { Panel } = Collapse;

// Hyphen-slug used for step ids (matches the imported forms.json style).
const slugStepId = (title) => (title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// A disabled arrow in a Panel header: antd icons take no `disabled`, so the
// unavailable direction is greyed and its handler is a no-op rather than the
// icon disappearing — a control that moves around as you use it is harder to
// aim at than one that stays put.
const DISABLED_ICON = { color: '#d0d5dd', cursor: 'not-allowed' };

// A stable, unique id for dragging, independent of the step's own `id`.
//
// The step id CANNOT be used: it is a slug of the title, so two steps called
// the same thing share one, and dnd-kit would move whichever it found first.
// Transient, like `_idEdited` — assembleSchema builds each saved step from
// named fields, so neither ever reaches the schema.
const makeDndId = () => `_dnd_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const emptyStep = (index) => {
  const title = `Step ${index + 1}`;
  // _idEdited is a transient flag (stripped on save): while false, the step id
  // auto-follows the title; once the user edits the id, it stops auto-updating.
  return { id: slugStepId(title), title, columns: 1, fields: [], _idEdited: false, _dndId: makeDndId() };
}

// schema_json -> the builder's step state. Shared by the initial seed and by
// "Reset to template default" so the two can never drift apart.
const stepsFromSchema = (schema) => {
  const loaded = (schema && Array.isArray(schema.steps) ? schema.steps : []).map((s) => ({
    id: s.id || slugStepId(s.title || ''),
    title: s.title || '',
    columns: (s.formMeta && s.formMeta.columns) || 1,
    fields: (s.formMeta && s.formMeta.fields) ? s.formMeta.fields : [],
    // An imported id is fixed; a title-derived one keeps auto-following.
    _idEdited: !!s.id,
    _dndId: makeDndId(),
  }));
  return loaded.length ? loaded : [emptyStep(0)];
};;

// mode 'library' (default): saves to the global Form Library via globalFormService.
// mode 'property': edits a per-property adopted check-in form — only Title + the
// step/field builder show; saving is delegated to `saveFn(data)` where
// data = { name, schema_json } (the caller binds property + form id).
// `formId` is the PER-PROPERTY form id, passed separately from `form`. The property
// Add-Form flow deliberately seeds `form` with a schema and NO id, so the builder can
// never target the library template it was cloned from — but a couple of features do
// need to know which per-property form is open (which fields its template protects,
// and what to reset it to). Those only ever READ, through per-property endpoints, so
// the "never target the template" invariant is untouched.
const LibraryFormBuilderDrawer = ({ visible, form: editForm, onClose, onSaved, mode = 'library', saveFn, propertyId, formId }) => {
  const isProperty = mode === 'property';
  const [metaForm] = Form.useForm();
  const [steps, setSteps] = useState([emptyStep(0)]);
  // Form-level marking. Held outside metaForm because it belongs to schema_json,
  // not to the form's database columns — and because the property-mode drawer
  // shows a trimmed metaForm that this must survive.
  const [scoringEnabled, setScoringEnabled] = useState(false);
  const [passMark, setPassMark] = useState(80);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fieldEditor, setFieldEditor] = useState({ visible: false, stepIndex: null, fieldIndex: null });
  // Keys the source library template marks protected. Only meaningful in property
  // (standalone) mode — a library author edits the template itself, so nothing is
  // locked for them. Resolved from the LIVE template by the backend, so locking a
  // field in the library applies to copies adopted before it was locked.
  const [protectedKeys, setProtectedKeys] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  // Property hosts (Manage Hosts) — the default set baked into unconfigured List
  // Hosts fields on save so they show all hosts.
  const [propertyHosts, setPropertyHosts] = useState([]);

  const isEdit = !!(editForm && editForm.id);
  // The saved per-property form behind this builder, however the caller supplied it.
  const propertyFormId = formId || (editForm && editForm.id) || null;

  const loadSubCategories = useCallback((categoryId) => {
    if (!categoryId) {
      setSubCategoryOptions([]);
      return;
    }
    subCategoryGlobalFormService.get(categoryId).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        setSubCategoryOptions((res.sub_category_global_forms || []).map((s) => ({ value: s.id, label: s.name })));
      }
    });
  }, []);

  // Populate on open.
  useEffect(() => {
    if (!visible) {
      return;
    }
    categoryGlobalFormService.get().then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        setCategoryOptions((res.category_global_form || []).map((c) => ({ value: c.id, label: c.name })));
      }
    });

    // Seed the builder when editing an existing form OR when a caller (e.g. the
    // property Add-Form "Edit form" flow) hands us a schema to customize without a
    // persisted id. The latter carries NO library id, so it can never target a template.
    const hasSeedSchema = !!(editForm && editForm.schema_json && Array.isArray(editForm.schema_json.steps) && editForm.schema_json.steps.length);
    if (isEdit || hasSeedSchema) {
      metaForm.setFieldsValue({
        name: editForm.name,
        type: editForm.type,
        category_global_form_id: editForm.category_global_form_id,
        sub_category_global_form_id: editForm.sub_category_global_form_id,
        is_live: editForm.is_live === 1,
      });
      loadSubCategories(editForm.category_global_form_id);
      setSteps(stepsFromSchema(editForm.schema_json));
      const seedScoring = (editForm.schema_json && editForm.schema_json.scoring) || null;
      setScoringEnabled(!!(seedScoring && seedScoring.enabled));
      setPassMark(seedScoring && seedScoring.passMark !== undefined ? seedScoring.passMark : 80);
    } else {
      metaForm.resetFields();
      metaForm.setFieldsValue({ is_live: false });
      setSteps([emptyStep(0)]);
      setSubCategoryOptions([]);
      setScoringEnabled(false);
      setPassMark(80);
    }
  }, [visible, editForm, isEdit, metaForm, loadSubCategories]);

  // Which keys the source template protects. Only the standalone builder needs it,
  // and only for a form that already exists (a not-yet-saved adoption carries the
  // template's own `protected` flags in its seed schema, which isFieldLocked reads).
  useEffect(() => {
    if (!visible || !isProperty || !propertyFormId) {
      setProtectedKeys([]);
      return;
    }
    formService.getProtectedFields(propertyFormId)
      .then((keys) => setProtectedKeys(keys || []));
  }, [visible, isProperty, propertyFormId]);

  // Per-property builder: load Manage Hosts data to default unconfigured List Hosts
  // fields to "show all hosts" on save.
  useEffect(() => {
    if (!visible || !isProperty || !propertyId) {
      return;
    }
    propertyHostService.getAdmins(propertyId).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        setPropertyHosts(res.data || []);
      }
    });
  }, [visible, isProperty, propertyId]);

  const handleCategoryChange = (value) => {
    metaForm.setFieldsValue({ sub_category_global_form_id: undefined });
    loadSubCategories(value);
  };

  // ---- step operations (immutable) ----
  const updateStep = (index, patch) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  // Title change auto-generates the step id until the id has been edited directly.
  const handleStepTitleChange = (index, title) => {
    setSteps((prev) => prev.map((s, i) => {
      if (i !== index) {
        return s;
      }
      const next = { ...s, title };
      if (!s._idEdited) {
        next.id = slugStepId(title);
      }
      return next;
    }));
  };

  const handleStepIdChange = (index, id) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, id, _idEdited: true } : s)));
  };

  // Steps (other than the current one) that a switch field can reference to hide.
  const stepOptionsForEditor = () => {
    const { stepIndex } = fieldEditor;
    return steps
      .map((s, i) => ({ value: s.id, label: s.title || `Step ${i + 1}`, index: i }))
      .filter((o) => o.value && o.index !== stepIndex)
      .map((o) => ({ value: o.value, label: o.label }));
  };

  const addStep = () => {
    setSteps((prev) => [...prev, emptyStep(prev.length)]);
  };

  const removeStep = (index) => {
    // Removing the step would remove the protected fields inside it, which the
    // backend rejects — block it here so the author is told now rather than on save.
    if (stepHasLockedField(steps[index] || {})) {
      message.warning('This step contains protected fields and cannot be removed.');
      return;
    }
    setSteps((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  // Shared by the step list and every field table, so both feel the same.
  const sensors = useSensors(
    useSensor(PointerSensor, DRAG_SENSOR_OPTIONS.pointer),
    useSensor(TouchSensor, DRAG_SENSOR_OPTIONS.touch),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Drop handlers. Both resolve position by IDENTITY rather than by index —
  // looking a row up by where it used to be is how a dropped item ends up
  // animating back to its old slot.
  const handleStepDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    setSteps((prev) => {
      const from = prev.findIndex((s) => s._dndId === active.id);
      const to = prev.findIndex((s) => s._dndId === over.id);
      return from === -1 || to === -1 ? prev : arrayMove(prev, from, to);
    });
  };

  const handleFieldDragEnd = (stepIndex) => ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    setSteps((prev) => prev.map((s, i) => {
      if (i !== stepIndex) {
        return s;
      }
      const from = s.fields.findIndex((f) => f.key === active.id);
      const to = s.fields.findIndex((f) => f.key === over.id);
      return from === -1 || to === -1 ? s : { ...s, fields: arrayMove(s.fields, from, to) };
    }));
  };

  /**
   * Move a step up or down. Order is the only thing that changes: a step keeps
   * its `id`, so a `hiddenId` switch pointing at it still points at it, and any
   * submission already recorded against it still lines up.
   *
   * Worth knowing when reordering: a field's visibility condition can only name
   * fields that come BEFORE it, so moving a step above one it depends on leaves
   * that condition referencing a field the person has not reached yet. Nothing
   * breaks — an unanswered field simply fails the condition — but the field
   * will stay hidden, which is why the warning below names it.
   */
  const moveStep = (index, direction) => {
    setSteps((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };
  // A field is locked when the template protects its key. The seed schema's own
  // `protected` flag is honoured too, so a brand-new adoption (not saved yet, so
  // there is no form id to look the template up by) locks correctly straight away.
  const isFieldLocked = (field) => isProperty && !!field && (
    !!field.protected || protectedKeys.indexOf(field.key) !== -1
  );

  const stepHasLockedField = (step) => (step.fields || []).some(isFieldLocked);

  // ---- field operations ----
  const openAddField = (stepIndex) => setFieldEditor({ visible: true, stepIndex, fieldIndex: null });
  const openEditField = (stepIndex, fieldIndex) => {
    const target = ((steps[stepIndex] || {}).fields || [])[fieldIndex];
    if (isFieldLocked(target)) {
      message.warning('This field is protected by the form template and cannot be edited.');
      return;
    }
    setFieldEditor({ visible: true, stepIndex, fieldIndex });
  };

  const onFieldEditorOk = (fieldObj) => {
    const { stepIndex, fieldIndex } = fieldEditor;
    setSteps((prev) => prev.map((s, i) => {
      if (i !== stepIndex) {
        return s;
      }
      const fields = fieldIndex === null
        ? [...s.fields, fieldObj]
        : s.fields.map((f, fi) => (fi === fieldIndex ? fieldObj : f));
      return { ...s, fields };
    }));
    setFieldEditor({ visible: false, stepIndex: null, fieldIndex: null });
  };

  const deleteField = (stepIndex, fieldIndex) => {
    const target = ((steps[stepIndex] || {}).fields || [])[fieldIndex];
    if (isFieldLocked(target)) {
      message.warning('This field is protected by the form template and cannot be removed.');
      return;
    }
    setSteps((prev) => prev.map((s, i) => (i === stepIndex ? { ...s, fields: s.fields.filter((_, fi) => fi !== fieldIndex) } : s)));
  };

  // Quick toggle of a field's Protected flag straight from the field table, so the
  // author does not have to open the field editor just to lock/unlock one field.
  // Library builder only — protection belongs to the template. Like every other
  // edit here it updates local state and is persisted by the drawer's Save.
  const toggleFieldProtected = (stepIndex, fieldIndex, checked) => {
    setSteps((prev) => prev.map((s, i) => {
      if (i !== stepIndex) {
        return s;
      }
      return {
        ...s,
        fields: s.fields.map((f, fi) => {
          if (fi !== fieldIndex) {
            return f;
          }
          if (checked) {
            return { ...f, protected: true };
          }
          // Drop the key entirely rather than storing `false`, so an unprotected
          // field looks exactly as it did before this feature existed.
          const { protected: _removed, ...rest } = f;
          return rest;
        }),
      };
    }));
  };

  // Locking is safe and immediate. UNLOCKING is not: it silently gives every
  // property that adopted this template permission to edit or delete the field,
  // including copies adopted long ago (protection is resolved from the live
  // template). That consequence is invisible from this screen, so confirm it.
  //
  // The Switch is controlled by `record.protected`, so simply returning here leaves
  // it visually ON until the author confirms — no flicker, no optimistic flip.
  const requestProtectedChange = (stepIndex, fieldIndex, checked, record) => {
    if (checked) {
      toggleFieldProtected(stepIndex, fieldIndex, true);
      return;
    }
    Modal.confirm({
      title: 'Remove protection from this field?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <span>
          Every property using this template will be able to edit or delete
          <strong> {record.label || record.key}</strong> in their own copy — including
          properties that adopted it before now. Existing answers are not affected.
        </span>
      ),
      okText: 'Remove protection',
      okType: 'danger',
      cancelText: 'Keep protected',
      onOk: () => toggleFieldProtected(stepIndex, fieldIndex, false),
    });
  };

  // "Reset to template default" — discard this property's customisations and
  // take the library template's CURRENT schema. Only meaningful for a standalone
  // copy, which is why it is property-mode + saved-form only.
  //
  // Like every other edit in this builder it replaces local state; nothing is
  // persisted until Save. That is deliberate — it leaves Cancel as the escape hatch
  // if the reset turns out not to be what the author wanted — and the dialog says so.
  const [resetting, setResetting] = useState(false);
  // Open when the form has no source template to reset to — the author picks one
  // from the library instead. `categoryId` pre-filters it to the form's own kind.
  const [templatePicker, setTemplatePicker] = useState({ visible: false, categoryId: null });

  const handleResetToTemplate = async () => {
    setResetting(true);
    let info;
    try {
      info = await formService.getTemplateSchema(propertyFormId);
    } finally {
      setResetting(false);
    }

    // No source template — it was hand-built, or its template has been deleted.
    // Rather than a dead end, let the author pick any published template of the
    // same category to sync this form to.
    if (!info || !info.has_template) {
      setTemplatePicker({ visible: true, categoryId: info ? info.category_global_form_id : null });
      return;
    }

    confirmApplyTemplate(info.schema_json, info.template_name, false);
  };

  /**
   * Confirm, then replace the builder's steps with a template's schema.
   *
   * @param schema        the template's schema_json
   * @param templateName  shown so the author can see which original they get
   * @param isDifferent   true when picking a DIFFERENT template rather than
   *                      restoring this form's own source
   */
  const confirmApplyTemplate = (schema, templateName, isDifferent) => {
    Modal.confirm({
      title: isDifferent ? 'Replace this form with the selected template?' : 'Reset this form to the template default?',
      icon: <ExclamationCircleOutlined />,
      okText: isDifferent ? 'Replace the form' : 'Reset the form',
      okType: 'danger',
      cancelText: 'Keep my changes',
      width: 520,
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            This form will be replaced with the current content of
            <strong>{templateName ? ` “${templateName}”` : ' the selected library template'}</strong>.
          </p>
          <p style={{ marginBottom: 8 }}>
            <strong>Every change this property has made will be lost</strong> — added
            fields, edited labels and options, reordered steps, and any steps of your
            own. They cannot be recovered.
          </p>
          <p style={{ marginBottom: 0, color: 'rgba(0, 0, 0, 0.55)' }}>
            The form name is kept. Previously submitted answers are not affected.
            Nothing is saved until you click Save.
          </p>
        </div>
      ),
      onOk: () => {
        // Deep clone: the builder mutates step/field objects in place, and the
        // fetched template payload must not be aliased into that state.
        setSteps(stepsFromSchema(JSON.parse(JSON.stringify(schema))));
        message.success(
          isDifferent
            ? 'Form replaced with the selected template. Click Save to keep it.'
            : 'Form reset to the template default. Click Save to keep it.'
        );
      },
    });
  };

  // A template chosen from the picker. get-lived-forms returns full models, so the
  // schema is already in hand — no second fetch.
  const handleTemplatePicked = (template) => {
    setTemplatePicker({ visible: false, categoryId: null });
    if (!template || !template.schema_json || !Array.isArray(template.schema_json.steps) || !template.schema_json.steps.length) {
      message.error('That template has no content to apply.');
      return;
    }
    confirmApplyTemplate(template.schema_json, template.name, true);
  };

  const moveField = (stepIndex, fieldIndex, direction) => {
    setSteps((prev) => prev.map((s, i) => {
      if (i !== stepIndex) {
        return s;
      }
      const target = fieldIndex + direction;
      if (target < 0 || target >= s.fields.length) {
        return s;
      }
      const fields = [...s.fields];
      const tmp = fields[fieldIndex];
      fields[fieldIndex] = fields[target];
      fields[target] = tmp;
      return { ...s, fields };
    }));
  };

  // Stable step id: keep the imported one, else derive a hyphen-slug from the
  // title so `hiddenId` references survive editing.
  const deriveStepId = (s, index) => {
    if (s.id) {
      return s.id;
    }
    const slug = (s.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return slug || `step-${index + 1}`;
  };

  const assembleSchema = () => ({
    steps: steps.map((s, index) => {
      const columns = s.columns || 1;
      const fields = s.fields.map((f) => {
        // colSpan reflects layout intent and is derived here, not stored on the
        // field in the editor: a `clear: 'both'` field in a multi-column step
        // spans the full row width. Strip any stale colSpan first, then re-derive.
        const { colSpan, ...rest } = f;
        if (columns > 1 && rest.clear === 'both') {
          return { ...rest, colSpan: columns };
        }
        return rest;
      });
      return { id: deriveStepId(s, index), title: s.title, formMeta: { columns, fields } };
    }),
    // Only written when marking is on, so an ordinary form's schema stays
    // exactly as it was before this feature existed.
    ...(scoringEnabled ? { scoring: { enabled: true, passMark } } : {}),
  });

  const currentFieldForEditor = () => {
    const { stepIndex, fieldIndex } = fieldEditor;
    if (stepIndex === null || fieldIndex === null) {
      return null;
    }
    return steps[stepIndex].fields[fieldIndex];
  };

  const existingKeysForEditor = () => {
    const { stepIndex, fieldIndex } = fieldEditor;
    if (stepIndex === null) {
      return [];
    }
    return steps[stepIndex].fields
      .filter((_, fi) => fi !== fieldIndex)
      .map((f) => f.key);
  };

  // Fields a visibility condition can reference: every field BEFORE the one being
  // edited (all earlier steps, plus earlier fields in the same step). Display-only
  // widgets (fixed_text / fixed_image) collect no value, so they're excluded.
  const priorFieldsForEditor = () => {
    const { stepIndex, fieldIndex } = fieldEditor;
    if (stepIndex === null) {
      return [];
    }
    const prior = [];
    steps.forEach((s, si) => {
      if (si > stepIndex) {
        return;
      }
      s.fields.forEach((f, fi) => {
        const isEarlier = si < stepIndex || (fieldIndex === null || fi < fieldIndex);
        if (!isEarlier || (si === stepIndex && fi === fieldIndex)) {
          return;
        }
        if (!f.key || f.widget === 'fixed_text' || f.widget === 'fixed_image') {
          return;
        }
        prior.push({ key: f.key, label: f.label, widget: f.widget, options: f.options });
      });
    });
    return prior;
  };

  // The { value, label } options of the form's Visitor Types field (widget
  // 'visitor-types', key 'visitor_types'), searched across all steps — feeds the
  // List Hosts "Required for visitor types" config. Empty when the form has none.
  const formVisitorTypeOptions = () => {
    for (let si = 0; si < steps.length; si += 1) {
      const found = steps[si].fields.find((f) => f && (f.widget === 'visitor-types' || f.key === 'visitor_types'));
      if (found && Array.isArray(found.options)) {
        return found.options
          .map((o) => (o && typeof o === 'object' ? { value: o.value, label: o.label } : { value: o, label: o }))
          .filter((o) => o.value !== undefined && o.value !== null && o.value !== '');
      }
    }
    return [];
  };

  const handleSave = () => {
    metaForm
      .validateFields()
      .then((values) => {
        const totalFields = steps.reduce((sum, s) => sum + s.fields.length, 0);
        if (totalFields === 0) {
          message.error('Add at least one field before saving');
          return;
        }
        setLoading(true);

        // Property mode NEVER writes to the global Form Library — persistence is
        // delegated to the caller's saveFn (a per-property update). This hard guard
        // guarantees editing a per-property form can never mutate the source
        // template, even if a caller forgot to pass saveFn.
        if (isProperty) {
          if (!saveFn) {
            setLoading(false);
            message.error('Cannot save: no save handler provided');
            return;
          }
          // Bake host defaults so an unconfigured List Hosts field shows all hosts.
          saveFn({ name: values.name, schema_json: bakeHostDefaults(assembleSchema(), propertyHosts) })
            .then((res) => {
              if (res && res.code === SUCCESS_CODE) {
                message.success('Form updated');
                if (onSaved) {
                  onSaved();
                }
                onClose();
              } else {
                message.error((res && res.message) || 'Failed to save form');
              }
            })
            .catch((error) => {
              console.error('Save form error:', error);
              message.error('Failed to save form');
            })
            .finally(() => {
              setLoading(false);
            });
          return;
        }

        const data = {
          global_form: {
            name: values.name,
            type: values.type,
            category_global_form_id: values.category_global_form_id || null,
            sub_category_global_form_id: values.sub_category_global_form_id || null,
            schema_json: assembleSchema(),
            is_live: values.is_live ? 1 : 0,
          },
        };
        globalFormService
          .storeOrUpdate(data, isEdit ? editForm.id : null)
          .then((res) => {
            if (res && res.code === SUCCESS_CODE) {
              message.success(isEdit ? 'Form updated' : 'Form created');
              if (onSaved) {
                onSaved();
              }
              onClose();
            } else {
              message.error((res && res.message) || 'Failed to save form');
            }
          })
          .catch((error) => {
            console.error('Save form error:', error);
            message.error('Failed to save form');
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {});
  };

  // Listeners go on a native element. An icon (and antd Tooltip, which clones
  // its child) does not reliably forward the pointer handlers dnd-kit needs.
  const dragHandle = (handleProps) => {
    const { style, onPointerDown, ...rest } = handleProps;
    return (
      <span
        {...rest}
        title="Drag to reorder"
        aria-label="Drag to reorder"
        style={{ ...style, display: 'inline-flex', padding: 4 }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => {
          if (onPointerDown) {
            onPointerDown(e);
          }
          e.stopPropagation();
        }}
      >
        <HolderOutlined />
      </span>
    );
  };

  return (
    <Drawer
      title={isProperty ? 'Edit Standalone Form' : (isEdit ? 'Edit Form' : 'Add Form')}
      width={920}
      visible={visible}
      onClose={onClose}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            {isProperty && propertyFormId && (
              <Button
                icon={<UndoOutlined />}
                danger
                loading={resetting}
                onClick={handleResetToTemplate}
              >
                Reset to template default
              </Button>
            )}
            <Button icon={<EyeOutlined />} onClick={() => setPreviewVisible(true)}>Preview</Button>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" className="btn-success" loading={loading} onClick={handleSave}>
              {isProperty ? 'Save' : (isEdit ? 'Update' : 'Create')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={metaForm} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="name" label="Title" rules={[{ required: true, whitespace: true, message: 'Title is required' }]}>
              <Input placeholder="Form title" />
            </Form.Item>
          </Col>
          {!isProperty && (
            <>
              <Col xs={24} md={12}>
                <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Type is required' }]}>
                  <Select options={LIBRARY_FORM_TYPE_OPTIONS} placeholder="Select type" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="category_global_form_id" label="Category">
                  <Select allowClear showSearch optionFilterProp="label" options={categoryOptions} placeholder="Select category" onChange={handleCategoryChange} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="sub_category_global_form_id" label="Sub-category">
                  <Select allowClear showSearch optionFilterProp="label" options={subCategoryOptions} placeholder="Select sub-category" disabled={!subCategoryOptions.length} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="is_live" label="Published" valuePropName="checked">
                  <Switch checkedChildren="Live" unCheckedChildren="Draft" />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Form>

      {/* Marking. Off by default and independent of the form's category: a form
          becomes an assessment by being attached to a training asset, not by
          being labelled one, so any form here can be graded. */}
      <div style={{ padding: '12px 14px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} md={12}>
            <Form.Item label="Automatic marking" style={{ marginBottom: 0 }} colon={false}>
              <Switch
                checked={scoringEnabled}
                onChange={setScoringEnabled}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
              <span style={{ marginLeft: 10, color: '#8c8c8c', fontSize: 12 }}>
                Score this form when it is used as a training assessment.
              </span>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Pass mark (%)" style={{ marginBottom: 0 }}>
              <InputNumber
                min={0}
                max={100}
                value={passMark}
                onChange={(v) => setPassMark(v === null || v === undefined ? 0 : v)}
                disabled={!scoringEnabled}
              />
              <span style={{ marginLeft: 10, color: '#8c8c8c', fontSize: 12 }}>
                Scoring this or above passes. Mark the correct answers on each question.
              </span>
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* ONE Collapse PER STEP, rather than one Collapse holding every panel.
          antd's Collapse reads its children looking for Panels, so a Panel
          wrapped in the sortable's positioned <div> is no longer a child it
          recognises and the accordion breaks. Splitting it costs nothing
          visually and has a bonus: each step's open/closed state now belongs
          to the step and travels with it, instead of staying behind at the
          position it was dragged from. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleStepDragEnd}
      >
        <SortableContext
          items={steps.map((s) => s._dndId)}
          strategy={verticalListSortingStrategy}
        >
        {steps.map((step, stepIndex) => (
        <SortableBlock key={step._dndId} id={step._dndId}>
          {({ handleProps }) => (
          <Collapse defaultActiveKey={stepIndex === 0 ? ['panel'] : []}>
          <Panel
            key="panel"
            header={step.title || `Step ${stepIndex + 1}`}
            extra={
              steps.length > 1 ? (
                // stopPropagation on every one of these: they sit in the Panel
                // header, and without it each click would also toggle the panel
                // open or shut under the pointer.
                <Space onClick={(e) => e.stopPropagation()}>
                  {dragHandle(handleProps)}
                  <Tooltip title="Move step up">
                    <ArrowUpOutlined
                      style={stepIndex === 0 ? DISABLED_ICON : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (stepIndex > 0) { moveStep(stepIndex, -1); }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Move step down">
                    <ArrowDownOutlined
                      style={stepIndex === steps.length - 1 ? DISABLED_ICON : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (stepIndex < steps.length - 1) { moveStep(stepIndex, 1); }
                      }}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="Remove this step?"
                    onConfirm={(e) => { if (e) { e.stopPropagation(); } removeStep(stepIndex); }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteOutlined onClick={(e) => e.stopPropagation()} />
                  </Popconfirm>
                </Space>
              ) : null
            }
          >
            <Row gutter={16}>
              <Col xs={24} md={9}>
                <Form.Item label="Step title">
                  <Input value={step.title} onChange={(e) => handleStepTitleChange(stepIndex, e.target.value)} placeholder="Step title" />
                </Form.Item>
              </Col>
              <Col xs={24} md={9}>
                <Form.Item
                  label="Step ID"
                  tooltip="Auto-generated from the title. Other steps' switch fields can reference this ID to hide this step."
                >
                  <Input value={step.id} onChange={(e) => handleStepIdChange(stepIndex, e.target.value)} placeholder="step-id" />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item label="Columns">
                  <Select
                    value={step.columns}
                    onChange={(value) => updateStep(stepIndex, { columns: value })}
                    options={[{ value: 1, label: '1 column' }, { value: 2, label: '2 columns' }]}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Div rows, not a table: CSS transforms are ignored on <tr>, so a
                drag inside antd Table never moves the row and the drop lands
                back where it started. Field keys are unique within a step. */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleFieldDragEnd(stepIndex)}
            >
              <SortableContext
                items={step.fields.map((f) => f.key)}
                strategy={verticalListSortingStrategy}
              >
                {step.fields.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 1.4fr 1.2fr 110px 80px 100px 150px', gap: 8, padding: '6px 8px', color: '#8c8c8c', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
                    <span />
                    <span>Label</span>
                    <span>Key</span>
                    <span>Type</span>
                    <span>Required</span>
                    <span>Protected</span>
                    <span>Actions</span>
                  </div>
                )}
                {step.fields.map((field, fieldIndex) => (
                  <SortableBlock key={field.key} id={field.key} compact>
                    {({ handleProps: fieldHandleProps }) => (
                      <div style={{ display: 'grid', gridTemplateColumns: '36px 1.4fr 1.2fr 110px 80px 100px 150px', gap: 8, alignItems: 'center', padding: '4px 8px', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
                        {dragHandle(fieldHandleProps)}
                        <span>{field.label}</span>
                        <span style={{ color: '#595959' }}>{field.key}</span>
                        <span>{getWidgetLabel(field.widget || 'text')}</span>
                        <span>{field.required ? 'Yes' : 'No'}</span>
                        {/* Protection, carried over from the table these rows
                            replaced. In a standalone (property) form it is
                            read-only and explains why the actions beside it are
                            disabled; in the library builder it is a toggle, so
                            locking a field does not require opening the field
                            editor. */}
                        <span>
                          {isProperty ? (
                            isFieldLocked(field)
                              ? <Tag icon={<LockOutlined />} color="default">Protected</Tag>
                              : null
                          ) : (
                            <Tooltip title={field.protected
                              ? 'Protected: properties that adopt this template cannot edit or delete this field'
                              : 'Not protected: properties can edit or delete this field in their own copy'}>
                              <Switch
                                size="small"
                                checked={!!field.protected}
                                onChange={(checked) => requestProtectedChange(stepIndex, fieldIndex, checked, field)}
                              />
                            </Tooltip>
                          )}
                        </span>
                        <Space>
                          <Button type="link" icon={<ArrowUpOutlined />} disabled={fieldIndex === 0} onClick={() => moveField(stepIndex, fieldIndex, -1)} />
                          <Button type="link" icon={<ArrowDownOutlined />} disabled={fieldIndex === step.fields.length - 1} onClick={() => moveField(stepIndex, fieldIndex, 1)} />
                          <Button type="link" icon={<EditOutlined />} onClick={() => openEditField(stepIndex, fieldIndex)} />
                          <Popconfirm title="Remove this field?" onConfirm={() => deleteField(stepIndex, fieldIndex)} okText="Yes" cancelText="No">
                            <Button type="link" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      </div>
                    )}
                  </SortableBlock>
                ))}
              </SortableContext>
            </DndContext>

            <Button type="dashed" icon={<PlusOutlined />} onClick={() => openAddField(stepIndex)} style={{ marginTop: 12 }} block>
              Add Field
            </Button>
          </Panel>
          </Collapse>
          )}
        </SortableBlock>
        ))}
        </SortableContext>
      </DndContext>

      <Button type="dashed" icon={<PlusOutlined />} onClick={addStep} style={{ marginTop: 12 }} block>
        Add Step
      </Button>

      <SelectFromLibraryModal
        visible={templatePicker.visible}
        categories={categoryOptions.map((c) => ({ id: c.value, name: c.label }))}
        initialCategoryId={templatePicker.categoryId}
        onCancel={() => setTemplatePicker({ visible: false, categoryId: null })}
        onSelect={handleTemplatePicked}
      />

      <FieldEditorModal
        visible={fieldEditor.visible}
        field={currentFieldForEditor()}
        existingKeys={existingKeysForEditor()}
        stepOptions={stepOptionsForEditor()}
        priorFields={priorFieldsForEditor()}
        visitorTypeOptions={formVisitorTypeOptions()}
        propertyId={propertyId}
        isProperty={isProperty}
        onOk={onFieldEditorOk}
        onCancel={() => setFieldEditor({ visible: false, stepIndex: null, fieldIndex: null })}
      />

      <PreviewFormModal
        visible={previewVisible}
        form={{
          name: metaForm.getFieldValue('name'),
          type: metaForm.getFieldValue('type'),
          schema_json: assembleSchema(),
        }}
        onCancel={() => setPreviewVisible(false)}
      />
    </Drawer>
  );
};

export default LibraryFormBuilderDrawer;
